import { create } from 'zustand';

type ItemPanelContentType = 'item-create' | 'help' | null;

// Form-related state
interface LabourItemFormState {
  addItemFormName: string;
}

// Panel state (replaces dialog for add item)
interface LabourItemPanelState {
  isPanelOpen: boolean;
  panelContent: ItemPanelContentType;
}

// Dialog-related state (only delete confirmation remains)
interface LabourItemDialogState {
  isDeleteDialogOpen: boolean;
  selectedItemIdForDeletion: string | null;
}

// Menu-related state
interface LabourItemMenuState {
  actionsMenuAnchorElement: HTMLElement | null;
  itemIdWithOpenActionsMenu: string | null;
}

// Combined state interface
interface LabourItemUIState
  extends LabourItemFormState,
    LabourItemPanelState,
    LabourItemDialogState,
    LabourItemMenuState {}

interface LabourItemUIActions {
  // Form actions
  setAddItemFormName: (name: string) => void;
  clearAddItemForm: () => void;

  // Panel actions (NEW)
  openItemCreatePanel: () => void;
  openHelp: () => void;
  closePanel: () => void;

  // Dialog actions
  setIsDeleteDialogOpen: (open: boolean) => void;
  setSelectedItemIdForDeletion: (id: string | null) => void;
  closeAllDialogs: () => void;

  // Menu actions
  setActionsMenuAnchorElement: (el: HTMLElement | null) => void;
  setItemIdWithOpenActionsMenu: (id: string | null) => void;
  closeActionsMenu: () => void;
}

export const useLabourItemUIStore = create<
  LabourItemUIState & LabourItemUIActions
>(set => {
  // Private helper functions for common UI operations
  const resetFormState = () => ({
    addItemFormName: '',
  });

  const resetPanelState = () => ({
    isPanelOpen: false,
    panelContent: null as ItemPanelContentType,
  });

  const resetDialogState = () => ({
    isDeleteDialogOpen: false,
    selectedItemIdForDeletion: null,
  });

  const resetMenuState = () => ({
    actionsMenuAnchorElement: null,
    itemIdWithOpenActionsMenu: null,
  });

  return {
    // State initialization
    ...resetFormState(),
    ...resetPanelState(),
    ...resetDialogState(),
    ...resetMenuState(),

    // Form actions
    setAddItemFormName: (name: string) => set({ addItemFormName: name }),

    clearAddItemForm: () => set(resetFormState()),

    // Panel actions
    openItemCreatePanel: () =>
      set({
        isPanelOpen: true,
        panelContent: 'item-create',
        addItemFormName: '', // Reset form when opening
      }),

    openHelp: () =>
      set({
        isPanelOpen: true,
        panelContent: 'help',
      }),

    closePanel: () =>
      set({
        ...resetPanelState(),
        addItemFormName: '', // Clear form when closing
      }),

    // Dialog actions
    setIsDeleteDialogOpen: (open: boolean) => set({ isDeleteDialogOpen: open }),

    setSelectedItemIdForDeletion: (id: string | null) =>
      set({ selectedItemIdForDeletion: id }),

    closeAllDialogs: () => set(resetDialogState()),

    // Menu actions
    setActionsMenuAnchorElement: (el: HTMLElement | null) =>
      set({ actionsMenuAnchorElement: el }),

    setItemIdWithOpenActionsMenu: (id: string | null) =>
      set({ itemIdWithOpenActionsMenu: id }),

    closeActionsMenu: () => set(resetMenuState()),
  };
});
